import { OktaAuth } from '@okta/okta-auth-js';
import envConfig from '../config/config';
import {
  isEhrOidcAuthMode,
  isEhrOktaCallbackPath,
  setEhrOidcAuthMode,
} from './okta-auth-mode';

export const oktaConfig = envConfig.okta;

export const issuer = `${oktaConfig.baseUrl}/oauth2/${oktaConfig.issuer}`;

/**
 * Persist EHR OIDC mode as early as possible on the OAuth callback route so the post-login
 * navigation (e.g. /?fromLogin=true) still resolves to the EHR client for token refresh.
 */
if (typeof window !== 'undefined' && isEhrOktaCallbackPath()) {
  setEhrOidcAuthMode();
}

/**
 * Resolve which Account UI OIDC application (default SPA vs EHR-only) to use for this browsing context.
 */
export function resolveAccountOidcOptions() {
  const useEhr =
    typeof window !== 'undefined' && (isEhrOidcAuthMode() || isEhrOktaCallbackPath());
  return {
    useEhr,
    clientId: useEhr ? envConfig.authorizedClientIds.accountUiEhr : envConfig.authorizedClientIds.accountUi,
    redirectPath: useEhr ? '/callback/ehr' : '/callback',
  };
}

export function getAccountOidcRedirectUri() {
  return `${getOauthBrowsingContextOrigin()}${resolveAccountOidcOptions().redirectPath}`;
}

/** Sync OktaAuth options when switching to EHR mode after the SDK was created (e.g. embedded iframe before redirect). */
export function applyResolvedOidcOptionsToOktaAuth(oktaAuth) {
  const { clientId, redirectPath } = resolveAccountOidcOptions();
  oktaAuth.options.clientId = clientId;
  oktaAuth.options.redirectUri = `${getOauthBrowsingContextOrigin()}${redirectPath}`;
}

/**
 * Origin for OAuth redirect_uri and Okta-react restoreOriginalUri. When Account UI runs in a frame,
 * prefer the top browsing context so Okta returns to the correct window. Cross-origin parents
 * cannot be read (accessing top.location throws); fall back to this frame.
 */
export function getOauthBrowsingContextOrigin() {
  try {
    if (window.top != null && window.top !== window.self) {
      return window.top.location.origin;
    }
  } catch {
    // cross-origin top
  }
  return window.location.origin;
}

/**
 * Navigate to Okta authorize URL in the top window when embedded so OAuth is not stuck in an iframe.
 * @param {string} href
 * @param {Window} [browsingWindow] defaults to global window (override in tests / non-browser)
 */
export function assignOauthRedirectLocation(href, browsingWindow = typeof window !== 'undefined' ? window : undefined) {
  if (!browsingWindow) {
    return;
  }
  try {
    if (browsingWindow.self !== browsingWindow.top) {
      browsingWindow.top.location.assign(href);
      return;
    }
  } catch {
    // Cross-origin parent: cannot navigate top; stay in this frame.
  }
  browsingWindow.location.assign(href);
}

const initialOidc = resolveAccountOidcOptions();
const oktaAuthConfiguration = {
  issuer,
  clientId: initialOidc.clientId,
  redirectUri: `${getOauthBrowsingContextOrigin()}${initialOidc.redirectPath}`,
  setLocation: assignOauthRedirectLocation,
};

export const oktaAuthJSClient = new OktaAuth(oktaAuthConfiguration);

export const REDIRECT_STORAGE_KEY = 'oidc-redirect';

export const USER_SESSION_INFO = 'user-session-info';
// Initial page with just email field
export const IDP_DISCOVERY = 'idp-discovery';
// Login + Password Page
export const PRIMARY_AUTH = 'primary-auth';
